import { createRouter, createWebHistory, type RouteLocation, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { dataViewForRoute, ENTRY_DIALOG_QUERY, LocalStorageUtils, routeConstants } from '@/utils';
import { useFormBuilder } from '@/stores/formBuilder';
import { useEntryStore } from '@/stores/entry';
import { useEntryListStore } from '@/stores/entryList';
import { useStudyStore } from '@/stores/study';

/**
 * Every route component is loaded on demand, and nothing here imports a barrel.
 *
 * Both halves matter. `@/views` and `@/components` are `export *` barrels covering the whole app, so
 * a single named import from either put every view, every component and their dependencies —
 * Chart.js, `primevue/chart`, `primevue/galleria` — into the entry chunk, for every route including
 * login. And because those dependencies were hoisted above the lazy boundary, the `() => import()`
 * calls that were already here saved almost nothing: the chunk they were meant to defer had already
 * been pulled into the one that loads first.
 *
 * So: barrels out, and `() => import()` for all of them rather than some. Named consts rather than
 * inline, because several of these serve more than one route and should resolve to one chunk.
 */
const ActivityContainer = () => import('@/views/ActivityContainer.vue');
const DashboardContainer = () => import('@/views/DashboardContainer.vue');
const ExportContainer = () => import('@/views/ExportContainer.vue');
const StudySettingsContainer = () => import('@/views/StudySettingsContainer.vue');
const ArchiveContainer = () => import('@/views/ArchiveContainer.vue');
const DataContainer = () => import('@/views/DataContainer.vue');
const UserManagementContainer = () => import('@/views/UserManagementContainer.vue');
const AccountSettingsContainer = () => import('@/views/AccountSettingsContainer.vue');
const OAuthStudyPagesContainer = () => import('@/views/OAuthStudypagesContainer.vue');
const SignUpContainer = () => import('@/views/SignUpContainer.vue');
const StudyCreateContainer = () => import('@/views/StudyCreateContainer.vue');
const PumpQcReportContainer = () => import('@/views/PumpQcReportContainer.vue');
const NotificationsContainer = () => import('@/views/NotificationsContainer.vue');
const LoginContainer = () => import('@/views/LoginContainer.vue');
const AnalyticsContainer = () => import('@/views/AnalyticsContainer.vue');
const ChildStudiesContainer = () => import('@/views/ChildStudiesContainer.vue');
const SurgeonDashboardContainer = () => import('@/views/SurgeonDashboardContainer.vue');
const StudyFetchContainer = () => import('@/views/StudyFetchContainer.vue');

const Onboarding = () => import('@/components/login/onboarding/Onboarding.vue');
const FormList = () => import('@/components/forms/FormList.vue');
const PrintFormView = () => import('@/components/forms/form_builder/print/PrintFormView.vue');
const FormBuilderContainer = () => import('@/components/forms/form_builder/FormBuilderContainer.vue');
const FormPreview = () => import('@/components/forms/FormPreview.vue');
const DataSection = () => import('@/components/data/DataSection.vue');
const CasesList = () => import('@/components/data/cases/CasesList.vue');
const Pipeline = () => import('@/components/data/pipeline/Pipeline.vue');
const EntryList = () => import('@/components/data/entries/EntryList.vue');
const Entry = () => import('@/components/data/entry/Entry.vue');
const PublicEntry = () => import('@/components/data/entry/PublicEntry.vue');
const InteractiveDashboard = () => import('@/components/surgeon_dashboard/interactive/InteractiveDashboard.vue');
const SurgeonDashboard = () => import('@/components/surgeon_dashboard/SurgeonDashboard.vue');
const Logout = () => import('@/components/login/Logout.vue');
const MobileAuth = () => import('@/components/login/mobile_auth/MobileAuth.vue');
const NotFound = () => import('@/components/NotFound.vue');

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * A form a respondent fills in without an account, at `/form/:id` or `/study/:id/form/:id`.
     * Read this, never the path: the builder's `/study/:id/dash/form/:id` contains `/form/` too.
     */
    publicEntry?: boolean;
    /**
     * The form preview at `/study/:id/dash/form/:id/preview`: an entry with no case that writes
     * nothing, sends no metrics and keeps its values in the entry store for the tab.
     */
    entryPreview?: boolean;
    /**
     * A route `DashboardContainer` renders with no top bar, sidebar or mobile drawer: the routed
     * view is the whole page. The shell still reads the studies for it.
     */
    chromeless?: boolean;
  }
}

type RouteFacts = Pick<RouteLocation, 'name' | 'meta' | 'fullPath'>;

/** Routes that render an entry, in the dashboard or in public. The print view renders none. */
const ENTRY_ROUTE_NAMES: ReadonlySet<unknown> = new Set([
  routeConstants.CASE_NEW_ENTRY.name,
  routeConstants.CASE_ENTRY.name,
  routeConstants.NEW_ENTRY.name,
  routeConstants.ENTRY.name,
  routeConstants.PUBLIC_FORM.name,
  routeConstants.STUDY_PUBLIC_FORM.name,
  routeConstants.FORM_PREVIEW.name
]);

/** Whether a navigation closes the entry it starts from, so its close event goes out. */
export const isLeavingEntryPage = (from: RouteFacts, to: RouteFacts) =>
  ENTRY_ROUTE_NAMES.has(from.name) && !ENTRY_ROUTE_NAMES.has(to.name);

/** Whether a navigation takes the reader into the entry routes from outside them. */
const isEnteringEntryPage = (from: RouteFacts, to: RouteFacts) =>
  !ENTRY_ROUTE_NAMES.has(from.name) && ENTRY_ROUTE_NAMES.has(to.name);

/** The lists a reader starts a new entry from: the three list views and one case's entries. */
const isEntryStartingList = (route: RouteFacts) =>
  dataViewForRoute(route.name) !== undefined || route.name === routeConstants.CASE.name;

/**
 * Whether a navigation leaves the form preview it starts from. The preview saved nothing, so what
 * was typed there is dropped rather than asked about.
 */
export const isLeavingFormPreview = (from: RouteFacts, to: RouteFacts) =>
  from.meta.entryPreview === true && from.fullPath !== to.fullPath;

/** Whether a route opens without a login. */
export const isUnauthRoute = (to: RouteFacts) => {
  const route = to.fullPath;
  return to.meta.publicEntry === true ||
    to.name === routeConstants.PRINT_FORM.name ||
    route.startsWith(routeConstants.LOGIN.path) ||
    route.startsWith(routeConstants.SIGNUP.path) ||
    route.startsWith('/studypages') ||
    route.startsWith('/investigation') ||
    route.startsWith(routeConstants.ECONSENT_VERIFICATION.path) ||
    route.startsWith(routeConstants.JOIN_STUDY.path) ||
    route.startsWith(routeConstants.MOBILE_AUTH.path);
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: routeConstants.ROOT.path,
      name: routeConstants.ROOT.name,
      component: { template: '<div/>' }
    },
    {
      path: routeConstants.LOGIN.path,
      name: routeConstants.LOGIN.name,
      meta: { title: routeConstants.LOGIN.title },
      component: LoginContainer
    },
    {
      path: routeConstants.SIGNUP.path,
      name: routeConstants.SIGNUP.name,
      meta: { title: routeConstants.SIGNUP.title },
      component: SignUpContainer
    },
    {
      path: routeConstants.LOGOUT.path,
      name: routeConstants.LOGOUT.name,
      meta: { title: routeConstants.LOGOUT.title },
      component: Logout
    },
    {
      path: routeConstants.STUDYPAGES_LOGIN_SUCCESS.path,
      name: routeConstants.STUDYPAGES_LOGIN_SUCCESS.name,
      meta: { title: routeConstants.STUDYPAGES_LOGIN_SUCCESS.title },
      component: OAuthStudyPagesContainer
    },
    {
      path: routeConstants.ONBOARDING.path,
      name: routeConstants.ONBOARDING.name,
      meta: { title: routeConstants.ONBOARDING.title },
      component: Onboarding
    },
    {
      path: routeConstants.STUDY_REDIRECT.path,
      name: routeConstants.STUDY_REDIRECT.name,
      meta: { title: routeConstants.STUDY_REDIRECT.title },
      component: OAuthStudyPagesContainer
    },
    {
      path: routeConstants.JOIN_STUDY.path,
      name: routeConstants.JOIN_STUDY.name,
      meta: { title: routeConstants.JOIN_STUDY.title },
      component: LoginContainer
    },
    {
      path: routeConstants.ECONSENT_VERIFICATION.path,
      name: routeConstants.ECONSENT_VERIFICATION.name,
      meta: { title: routeConstants.ECONSENT_VERIFICATION.title },
      component: OAuthStudyPagesContainer
    },
    {
      path: routeConstants.STUDY_FETCH.path,
      name: routeConstants.STUDY_FETCH.name,
      meta: { title: routeConstants.STUDY_FETCH.title },
      component: StudyFetchContainer
    },
    {
      path: '/study/:studyIdentifier', // Base path for a study
      component: DashboardContainer,
      children: [
        {
          path: 'dash', // The dashboard itself
          name: routeConstants.DASHBOARD.name,
          // You might want a component here or a redirect to a default child like 'data'
          redirect: to => {
            return { name: routeConstants.DATA.name, params: { studyIdentifier: to.params.studyIdentifier } };
          },
          children: [
            {
              path: routeConstants.DATA.path,
              component: DataContainer,
              name: routeConstants.DATA.name,
              meta: { title: routeConstants.DATA.title },
              children: [
                /**
                 * The list views, which share the section's chrome — its header, the view switcher,
                 * the case search — so they render into `DataSection`'s outlet.
                 *
                 * Four of these records used to name `CasesList` and none of the four decided
                 * anything: a ref in `DataSection`, seeded from a localStorage key, picked the view
                 * and the URL followed it around. The record says what renders now.
                 */
                {
                  path: routeConstants.DATA_VIEWS.path,
                  component: DataSection,
                  name: routeConstants.DATA_VIEWS.name,
                  children: [
                    {
                      path: routeConstants.CASES.path,
                      component: CasesList,
                      name: routeConstants.CASES.name
                    },
                    {
                      path: routeConstants.CASES_PIPELINE.path,
                      component: Pipeline,
                      name: routeConstants.CASES_PIPELINE.name
                    },
                    /**
                     * The older URL for the entries view. Nothing navigates here any more — the
                     * switcher and the arrival redirect both target `ENTRIES` — but it stays
                     * matched because it is in bookmarks and in browser history.
                     */
                    {
                      path: routeConstants.CASE_ENTRIES.path,
                      component: EntryList,
                      name: routeConstants.CASE_ENTRIES.name
                    },
                    {
                      path: routeConstants.ENTRIES.path,
                      component: EntryList,
                      name: routeConstants.ENTRIES.name
                    },
                    /** One case's entries. The same list, over a case rather than the study. */
                    {
                      path: routeConstants.CASE.path,
                      component: EntryList,
                      name: routeConstants.CASE.name,
                      meta: { title: routeConstants.CASE.title }
                    }
                  ]
                },
                /**
                 * An entry fills the section, so these are siblings of the list layout rather than
                 * children of it. The case-scoped pair carries its own case segment for that
                 * reason; both URLs are what they always were.
                 */
                {
                  path: routeConstants.CASE_NEW_ENTRY.path,
                  component: Entry,
                  name: routeConstants.CASE_NEW_ENTRY.name
                },
                {
                  path: routeConstants.CASE_ENTRY.path,
                  component: Entry,
                  name: routeConstants.CASE_ENTRY.name
                },
                {
                  path: routeConstants.NEW_ENTRY.path,
                  component: Entry,
                  name: routeConstants.NEW_ENTRY.name
                },
                {
                  path: routeConstants.ENTRY.path,
                  component: Entry,
                  name: routeConstants.ENTRY.name
                }
              ]
            },
            {
              path: routeConstants.FORMS.path,
              component: FormList,
              name: routeConstants.FORMS.name,
              meta: { title: routeConstants.FORMS.title }
            },
            {
              path: routeConstants.ARCHIVE.path,
              component: ArchiveContainer,
              name: routeConstants.ARCHIVE.name,
              meta: { title: routeConstants.ARCHIVE.title }
            },
            {
              path: routeConstants.ANALYTICS.path,
              component: AnalyticsContainer,
              name: routeConstants.ANALYTICS.name,
              meta: { title: routeConstants.ANALYTICS.title }
            },
            /**
             * An admin study's counts per child study. No guard of its own: the read behind it is
             * gated on `VIEW_CASES`, and its 403 is what the section draws.
             */
            {
              path: routeConstants.CHILD_STUDIES.path,
              component: ChildStudiesContainer,
              name: routeConstants.CHILD_STUDIES.name,
              meta: { title: routeConstants.CHILD_STUDIES.title }
            },
            {
              path: routeConstants.SURGEON_DASHBOARD.path,
              component: SurgeonDashboardContainer,
              name: routeConstants.SURGEON_DASHBOARD.name,
              meta: { title: routeConstants.SURGEON_DASHBOARD.title },
              children: [
                {
                  path: '',
                  component: SurgeonDashboard,
                  name: routeConstants.DEFAULT_SURGEON_DASHBOARD.name,
                  meta: { title: routeConstants.SURGEON_DASHBOARD.title }
                },
                {
                  path: routeConstants.CASE_SURGEON_DASHBOARD.path,
                  component: SurgeonDashboard,
                  name: routeConstants.CASE_SURGEON_DASHBOARD.name,
                  meta: { title: routeConstants.SURGEON_DASHBOARD.title }
                },
                {
                  path: routeConstants.INTERACTIVE_SURGEON_DASHBOARD.path,
                  component: InteractiveDashboard,
                  name: routeConstants.INTERACTIVE_SURGEON_DASHBOARD.name,
                  meta: { title: routeConstants.INTERACTIVE_SURGEON_DASHBOARD.title }
                }
              ]
            },
            {
              path: routeConstants.PUMP_QC_REPORT.path,
              component: PumpQcReportContainer,
              name: routeConstants.PUMP_QC_REPORT.name,
              meta: { title: routeConstants.PUMP_QC_REPORT.title }
            },
            {
              path: routeConstants.FORM_BUILDER.path,
              component: FormBuilderContainer,
              name: routeConstants.FORM_BUILDER.name,
              meta: { title: routeConstants.FORM_BUILDER.title }
            },
            {
              path: routeConstants.FORM_PREVIEW.path,
              component: FormPreview,
              name: routeConstants.FORM_PREVIEW.name,
              meta: { title: routeConstants.FORM_PREVIEW.title, entryPreview: true, chromeless: true }
            },
            {
              path: routeConstants.EXPORT.path,
              component: ExportContainer,
              name: routeConstants.EXPORT.name,
              meta: { title: routeConstants.EXPORT.title }
            },
            {
              path: routeConstants.ACTIVITY.path,
              component: ActivityContainer,
              name: routeConstants.ACTIVITY.name,
              meta: { title: routeConstants.ACTIVITY.title }
            },
            {
              path: routeConstants.NOTIFICATIONS.path,
              component: NotificationsContainer,
              name: routeConstants.NOTIFICATIONS.name,
              meta: { title: routeConstants.NOTIFICATIONS.title }
            },
            {
              path: routeConstants.MANAGE.path,
              component: UserManagementContainer,
              name: routeConstants.MANAGE.name,
              meta: { title: routeConstants.MANAGE.title }
            },
            {
              path: routeConstants.STUDY_SETTINGS.path,
              component: StudySettingsContainer,
              name: routeConstants.STUDY_SETTINGS.name,
              meta: { title: routeConstants.STUDY_SETTINGS.title }
            },
            {
              path: routeConstants.ACCOUNT_SETTINGS.path,
              component: AccountSettingsContainer,
              name: routeConstants.ACCOUNT_SETTINGS.name,
              meta: { title: routeConstants.ACCOUNT_SETTINGS.title },
              children: [
                {
                  path: routeConstants.PART_11_PIN_SETUP.path,
                  component: StudySettingsContainer,
                  name: routeConstants.PART_11_PIN_SETUP.name,
                  meta: { title: routeConstants.PART_11_PIN_SETUP.title }
                }
              ]
            },
            {
              path: routeConstants.STUDY_CREATE.path,
              component: StudyCreateContainer,
              name: routeConstants.STUDY_CREATE.name,
              meta: { title: routeConstants.STUDY_CREATE.title }
            }
          ]
        }
      ]
    },
    {
      path: routeConstants.PUBLIC_FORM.path,
      name: routeConstants.PUBLIC_FORM.name,
      meta: { publicEntry: true },
      component: PublicEntry
    },
    {
      path: routeConstants.STUDY_PUBLIC_FORM.path,
      name: routeConstants.STUDY_PUBLIC_FORM.name,
      meta: { publicEntry: true },
      component: PublicEntry
    },
    {
      path: routeConstants.PRINT_FORM.path,
      name: routeConstants.PRINT_FORM.name,
      component: PrintFormView
    },
    {
      path: routeConstants.MOBILE_AUTH.path,
      name: routeConstants.MOBILE_AUTH.name,
      component: MobileAuth
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: NotFound
    }
  ]
});

/**
 * The list as the reader left it, for a create to go back to, without an entry dialog's query keys:
 * going back must not open the pipeline's dialog again.
 */
const listToReturnTo = (list: RouteLocationNormalized) => {
  const query = { ...list.query };
  Object.values(ENTRY_DIALOG_QUERY).forEach(key => delete query[key]);
  return router.resolve({ path: list.path, query, hash: list.hash }).fullPath;
};

/** The global guard, exported so a test can hand it two resolved routes without loading their views. */
export const guardNavigation: Parameters<typeof router.beforeEach>[0] = (to, from, next) => {
  const formBuilderStore = useFormBuilder();
  const entryStore = useEntryStore();
  const entryListStore = useEntryListStore();
  const authStore = useAuthStore();

  // Handle root path redirection here
  if (to.name === routeConstants.ROOT.name) {
    authStore.checkAuth();
    if (authStore.userLoggedIn) {
      const studyStore = useStudyStore();
      const studyId = LocalStorageUtils.getStudyIdentifier() || studyStore.selectedStudyId;
      if (studyId) {
        return next({
          name: routeConstants.DASHBOARD.name,
          params: { studyIdentifier: studyId }
        });
      }
      // If no studyId, go fetch studies
      return next({ name: routeConstants.STUDY_FETCH.name });
    }
    // If not logged in, go to login
    return next({ name: routeConstants.LOGIN.name });
  }

  // The three list views are the study's cases, entries or pipeline — never one case's. Leaving a
  // case for any of them drops the selection, so the header stops showing a case title and
  // `EntryList` reads the study rather than the case it was last opened from.
  if (dataViewForRoute(to.name)) {
    entryListStore.selectedCase = undefined;
  }

  if (isLeavingFormPreview(from, to)) {
    entryStore.closeFormPreview();
  }

  // Nothing at the edge of the entry routes happens until the guard lets the reader go. A reader who
  // stays on a dirty entry is still in its session, and a save from the prompt belongs to the session
  // it saves in.
  const crossEntryRoutesEdge = () => {
    if (isLeavingEntryPage(from, to)) {
      entryStore.closeEntrySession();
    }
    // The guard records the list a create goes back to as the reader comes in. An open never sees the
    // route before it, and each one resets the per-entry state, so the record has to outlive the
    // moves from entry to entry.
    if (isEnteringEntryPage(from, to)) {
      entryStore.recordStartedFrom(isEntryStartingList(from) ? listToReturnTo(from) : undefined);
    }
  };

  if (!entryStore.isEntryPristine) {
    entryStore.showDiscardChangesDialog()
      .then(() => {
        crossEntryRoutesEdge();
        next();
      })
      .catch(() => next(false));
  } else if (!formBuilderStore.isFormPristine) {
    formBuilderStore.showDiscardChangesDialog()
      .then(() => {
        crossEntryRoutesEdge();
        next();
      })
      .catch(() => next(false));
  } else {
    crossEntryRoutesEdge();
    authStore.checkAuth();
    // the 2nd condition is for public form entry
    if (!authStore.userLoggedIn && !isUnauthRoute(to)) {
      return next({
        name: routeConstants.LOGIN.name,
        query: { next: to.fullPath }
      });
    } else if (authStore.userLoggedIn) {
      if (to.name === routeConstants.LOGIN.name) {
        return next({
          name: routeConstants.STUDY_FETCH.name
        });
      }
    }
    if (to.meta.title) {
      document.title = `${to.meta.title} | Studypages Data`;
    }
    next();
  }
};

router.beforeEach(guardNavigation);

export default router;
